import get from 'lodash/get';
import isNil from 'lodash/isNil';
import unionBy from 'lodash/unionBy';

import {
  loadGlobalMessagingThread,
  findGlobalMessagingThreadWith,
  markGlobalMessagingThreadUnread,
} from 'modules/global-messages-threads';

import {
  fetchGlobalMessages,
  fetchUnreadMessagesCount,
  postGlobalMessage,
} from './api';
import { filterGlobalMessages, byThread } from './services';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadGlobalMessages = createAsyncThunk(
  'globalMessaging/loadMessages',
  async threadId => await fetchGlobalMessages(threadId),
);

export const loadUnreadMessagesCount = createAsyncThunk(
  'globalMessaging/loadUnreadMessagesCount',
  async _ => await fetchUnreadMessagesCount(),
);

export const sendGlobalMessage = createAsyncThunk(
  'globalMessaging/sendMessage',
  async ({ createdThreadId, text }) =>
    await postGlobalMessage(createdThreadId, text),
);

export const markGlobalMessagingThreadAsRead = (
  threadId,
  oppositeAccountId,
) => dispatch => {
  return dispatch(
    markGlobalMessagingThreadUnread({ threadId, oppositeAccountId: null }),
  ).then(() =>
    Promise.all([
      dispatch(loadGlobalMessagingThread({ accountId: oppositeAccountId })),
      dispatch(loadUnreadMessagesCount()),
    ]),
  );
};

export function loadGlobalMessagesWith(
  accountId,
  oppositeAccountId,
  oppositeAccountType,
) {
  return dispatch =>
    dispatch(loadGlobalMessagingThread({ accountId: oppositeAccountId })).then(
      ({ payload }) => {
        const thread = findGlobalMessagingThreadWith(
          oppositeAccountId,
          oppositeAccountType,
          payload.data,
        );

        if (isNil(thread)) {
          return Promise.resolve([]);
        }

        const threadId = get(thread, 'id');
        const unreadMessageAccountId = get(thread, 'unreadMessageAccountId');

        return Promise.all([
          dispatch(loadGlobalMessages(threadId)),
          unreadMessageAccountId === accountId &&
            dispatch(
              markGlobalMessagingThreadAsRead(threadId, oppositeAccountId),
            ),
        ]);
      },
    );
}

export const refreshGlobalMessagingWindow = (accountId, chatWindow) => {
  const { oppositeAccountId, oppositeAccountType } = chatWindow;
  return loadGlobalMessagesWith(
    accountId,
    oppositeAccountId,
    oppositeAccountType,
  );
};

const globalMessagesSlice = createSlice({
  name: 'globalMessaging',
  initialState: {
    messages: [],
    unreadMessagesCount: 0,
    loadingMessages: false,
  },
  extraReducers: builder => {
    builder
      .addCase(loadGlobalMessages.pending, (state, _) => {
        state.loadingMessages = true;
      })
      .addCase(loadGlobalMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;

        // state.messages MUST come first so that existing messages are not
        // reported as "new" due to being new objects
        state.messages = unionBy(state.messages, action.payload.data, 'id');
      })
      .addCase(loadUnreadMessagesCount.fulfilled, (state, action) => {
        state.unreadMessagesCount = get(
          action,
          'payload.data.unreadMessageThreadCount',
        );
      })
      .addCase(sendGlobalMessage.fulfilled, (state, action) => {
        state.messages = [...state.messages, action.payload.data];
      });
  },
  selectors: {
    getGlobalMessages: state => state.messages,
    getUnreadMessagesCount: state => state.unreadMessagesCount,
  },
});
export const reducer = globalMessagesSlice.reducer;
export const {
  getGlobalMessages,
  getUnreadMessagesCount,
} = globalMessagesSlice.selectors;

export const getMessages = threadId =>
  createSelector(
    getGlobalMessages,
    messages => filterGlobalMessages(messages, byThread(threadId)),
  );
